export type RegionSlug = 'central' | 'north' | 'east' | 'south' | 'west'

export type RegionConfig = {
	name: string
	slug: RegionSlug
	vibe: string
	areas: string
	areaSlugs: string[]
	img: string
}

// Imagery URLs lean on high-contrast architectural details to keep things editorial.
export const REGIONS: RegionConfig[] = [
	{
		name: 'Central',
		slug: 'central',
		vibe: 'The Heart',
		areas: 'Soho, Covent Garden, Mayfair, Fitzrovia, Marylebone, Westminster',
		areaSlugs: [
			'aldgate',
			'aldgate-east',
			'belgravia',
			'bloomsbury',
			'chinatown',
			'city-of-london',
			'clerkenwell',
			'covent-garden',
			'farringdon',
			'fitzrovia',
			'holborn',
			'kings-cross',
			'knightsbridge',
			'marylebone',
			'mayfair',
			'paddington',
			'piccadilly-circus',
			'pimlico',
			'soho',
			'south-bank',
			'st-jamess',
			'st-jamess-/-piccadilly',
			'strutton-ground-market',
			'trafalgar-square',
			'victoria',
			'westminster',
		],
		img: 'https://images.unsplash.com/photo-1543691110-1c7d6b1927f6?q=80&w=1200&auto=format&fit=crop',
	},
	{
		name: 'North',
		slug: 'north',
		vibe: 'The Locals',
		areas: 'Camden, Kentish Town, Tufnell Park, Archway, Finsbury Park, Islington',
		areaSlugs: [
			'angel',
			'archway',
			'belsize-park',
			'camden',
			'canonbury',
			'crouch-end',
			'edgware',
			'enfield',
			'finchley-road',
			'finsbury-park',
			'golders-green',
			'gospel-oak',
			'hampstead',
			'harringay',
			'harrow',
			'hatch-end',
			'hendon',
			'highbury',
			'highgate',
			'holloway',
			'hornsey',
			'islington',
			'kensal-rise',
			'kentish-town',
			'kenton',
			'kilburn',
			'kingsbury',
			'maida-vale',
			'muswell-hill',
			'new-barnet',
			'newington-green',
			'north-harrow',
			'primrose-hill',
			'queens-park',
			'queensbury',
			'rayners-lane',
			'southgate',
			'stanmore',
			'stoke-newington',
			'stroud-green',
			'tottenham',
			'tufnell-park',
			'west-hampstead',
			'wembley',
			'willesden',
			'willesden-green',
		],
		img: 'https://images.unsplash.com/photo-1561566302-67abce51c2c3?q=80&w=1200&auto=format&fit=crop',
	},
	{
		name: 'East',
		slug: 'east',
		vibe: 'The Edge',
		areas: 'Shoreditch, Hackney, Dalston, Bethnal Green, Whitechapel',
		areaSlugs: [
			'bethnal-green',
			'bow',
			'bromley-by-bow',
			'canary-wharf',
			'city-(old-street)',
			'clapton',
			'dalston',
			'de-beauvoir',
			'east-ham',
			'epping-forest',
			'hackney',
			'hackney-wick',
			'haggerston',
			'homerton',
			'hoxton',
			'leyton',
			'leytonstone',
			'limehouse',
			'london-fields',
			'plaistow',
			'poplar',
			'roman-road',
			'shoreditch',
			'spitalfields',
			'stratford',
			'upton-park',
			'victoria-park',
			'walthamstow',
			'wapping',
			'whitechapel',
			'woolwich',
		],
		img: 'https://images.unsplash.com/photo-1623549301882-e4724203c2c1?q=80&w=1200&auto=format&fit=crop',
	},
	{
		name: 'South',
		slug: 'south',
		vibe: 'The Soul',
		areas: 'Bermondsey, Peckham, Brixton, Dulwich, Greenwich, Deptford',
		areaSlugs: [
			'balham',
			'battersea',
			'bermondsey',
			'blackheath',
			'borough',
			'brixton',
			'brockley',
			'camberwell',
			'catford',
			'charlton',
			'clapham',
			'deptford',
			'dulwich',
			'east-dulwich',
			'elephant-&-castle',
			'forest-hill',
			'greenwich',
			'herne-hill',
			'honor-oak',
			'kennington',
			'lewisham',
			'new-cross',
			'nine-elms',
			'norwood',
			'nunhead',
			'oval',
			'peckham',
			'selhurst',
			'southwark',
			'stockwell',
			'streatham',
			'streatham-hill',
			'sydenham',
			'tooting',
			'vauxhall',
			'walworth',
			'waterloo',
		],
		img: 'https://images.unsplash.com/photo-1687803986235-990e21394233?q=80&w=1200&auto=format&fit=crop',
	},
	{
		name: 'West',
		slug: 'west',
		vibe: 'The Scene',
		areas: 'Notting Hill, Chelsea, Kensington, Fulham, Hammersmith',
		areaSlugs: [
			'acton',
			'bayswater',
			'brentford',
			'chelsea',
			'chiswick',
			'ealing',
			'earls-court',
			'east-acton',
			'fulham',
			'hammersmith',
			'harlesden',
			'holland-park',
			'hounslow',
			'kensington',
			'new-malden',
			'north-acton',
			'north-kensington',
			'northfields',
			'notting-hill',
			'park-royal',
			'putney',
			"queensway",
			'sheen',
			'shepherds-bush',
			'south-ealing',
			'south-kensington',
			'southall',
			'west-kensington',
			'white-city',
			'wimbledon',
			'wimbledon-park',
		],
		img: 'https://images.unsplash.com/photo-1729897671630-b335eca8045f?q=80&w=1200&auto=format&fit=crop',
	},
]

export const REGION_BY_SLUG = Object.fromEntries(REGIONS.map((region) => [region.slug, region])) as Record<
	RegionSlug,
	RegionConfig
>

export const AREA_TO_REGION = REGIONS.reduce<Record<string, RegionSlug>>((acc, region) => {
	region.areaSlugs.forEach((slug) => {
		acc[slug] = region.slug
	})
	return acc
}, {})

export function getRegionBySlug(slug: string | undefined) {
	if (!slug) return undefined
	return REGIONS.find((region) => region.slug === slug)
}
